import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import BoltIconPurple from '../../assets/svg/bolt-purple.svg';
import CloseIcon from '../../assets/svg/close.svg';
import { KeyCap, QuickLauncherHelpCard, QuickLauncherResultButton, ResultList } from './quick-launcher.shared';
import { getQuickLauncherDestinationKey } from './quick-launcher.util';
import { useQuickLauncherJump } from './use-quick-launcher-jump';

const FOCUSABLE_ELEMENT_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Overlay = styled.div`
  align-items: flex-start;
  background: rgba(25, 21, 43, 0.44);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 112px 24px 24px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 10000;
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid rgba(58, 49, 94, 0.1);
  border-radius: 10px;
  box-shadow: 0 24px 70px rgba(25, 21, 43, 0.24), 0 2px 8px rgba(25, 21, 43, 0.12);
  max-width: 520px;
  overflow: hidden;
  width: 100%;
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  padding: 10px 12px 0;
`;

const CloseButton = styled.button`
  background: transparent url(${CloseIcon}) center center no-repeat;
  background-size: 12px;
  border: none;
  border-radius: 18px;
  cursor: pointer;
  height: 36px;
  margin: -8px -8px -8px 12px;
  outline: none;
  width: 36px;

  &:focus,
  &:hover {
    background-color: ${(props) => props.theme.colors.bgAccent};
  }
`;

const Form = styled.form`
  padding: 18px 22px 22px;
`;

const InputWrap = styled.div`
  position: relative;

  &:before {
    background: transparent url(${BoltIconPurple}) center center no-repeat;
    background-size: 14px;
    content: '';
    display: block;
    height: 16px;
    left: 16px;
    opacity: 0.72;
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
  }
`;

const LauncherInput = styled.input`
  background: #fff;
  border: 1px solid rgba(58, 49, 94, 0.16);
  border-radius: 22px;
  box-shadow: inset 0 1px 4px rgba(25, 21, 43, 0.08);
  box-sizing: border-box;
  color: ${(props) => props.theme.colors.body};
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.9375em;
  height: 44px;
  letter-spacing: 0;
  outline: none;
  padding: 0 18px 0 44px;
  width: 100%;

  ::placeholder {
    color: ${(props) => props.theme.colors.placeholder};
    font-family: ${(props) => props.theme.typography.face.secondary};
  }

  &:focus {
    border-color: ${(props) => props.theme.colors.primaryBrand};
    box-shadow: 0 0 0 3px rgba(58, 49, 94, 0.1), inset 0 1px 4px rgba(25, 21, 43, 0.08);
  }
`;

const LauncherResults = styled(ResultList)`
  margin-top: 12px;
`;

const LauncherHelpCard = styled(QuickLauncherHelpCard)`
  margin-top: 12px;
`;

const ErrorMessage = styled.div`
  color: ${(props) => props.theme.colors.negative};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 0.875em;
  letter-spacing: 0;
  margin-top: 10px;
`;

const QuickLauncher: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { attemptJump, destinations, error, handleChange, hasValue, jumpTo, reset, value } = useQuickLauncherJump();

  const closeLauncher = useCallback(() => {
    setIsOpen(false);
    reset();

    if (previouslyFocusedElementRef.current) {
      previouslyFocusedElementRef.current.focus();
      previouslyFocusedElementRef.current = null;
    }
  }, [reset]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();

      if (!((event.metaKey || event.ctrlKey) && key === 'k')) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const isTypingElsewhere =
        !isOpen && !!target && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable);

      if (isTypingElsewhere) {
        return;
      }

      event.preventDefault();

      if (isOpen) {
        closeLauncher();
        return;
      }

      previouslyFocusedElementRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setIsOpen(true);
      reset();
    };

    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, [closeLauncher, isOpen, reset]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeLauncher();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) {
        return;
      }

      const focusableElements = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENT_SELECTOR));

      if (!focusableElements.length) {
        event.preventDefault();
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;
      const focusIsOutsidePanel = !(activeElement instanceof Node) || !panelRef.current.contains(activeElement);

      if (event.shiftKey && (activeElement === firstFocusableElement || focusIsOutsidePanel)) {
        event.preventDefault();
        lastFocusableElement.focus();
      } else if (!event.shiftKey && (activeElement === lastFocusableElement || focusIsOutsidePanel)) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeLauncher, isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (attemptJump()) {
      closeLauncher();
    }
  };

  const handleOverlayMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      closeLauncher();
    }
  };

  return (
    <Overlay onMouseDown={handleOverlayMouseDown}>
      <Panel ref={panelRef} role="dialog" aria-modal="true" aria-label="Quick launcher">
        <Header>
          <CloseButton type="button" aria-label="Close launcher" onClick={closeLauncher} />
        </Header>
        <Form onSubmit={handleSubmit}>
          <InputWrap>
            <LauncherInput
              ref={inputRef}
              aria-label="Jump to Reposit, Claim, Org, User, or email"
              autoComplete="off"
              data-1p-ignore="true"
              onChange={handleChange}
              placeholder="Enter an ID or user email"
              value={value}
            />
          </InputWrap>

          {destinations.length ? (
            <LauncherResults>
              {destinations.map((item) => (
                <QuickLauncherResultButton
                  key={getQuickLauncherDestinationKey(item)}
                  destination={item}
                  onClick={() => {
                    jumpTo(item);
                    closeLauncher();
                  }}
                />
              ))}
            </LauncherResults>
          ) : null}

          {!destinations.length && !hasValue ? (
            <LauncherHelpCard
              footer={
                <React.Fragment>
                  <KeyCap>Esc</KeyCap> closes this launcher.
                </React.Fragment>
              }
            />
          ) : null}

          {error ? <ErrorMessage>{error}</ErrorMessage> : null}
        </Form>
      </Panel>
    </Overlay>
  );
};

export default QuickLauncher;
